import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY, PRODUCTS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    product_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", product_interest: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check the fields.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-28 pb-24">
        <div className="grid md:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT — narrative */}
          <div className="md:col-span-5">
            <p className="overline">Say hello</p>
            <h1 className="mt-6 font-serif text-6xl md:text-7xl leading-[0.95] tracking-tighter">
              Reach <span className="italic text-[#C27A62]">out.</span>
            </h1>
            <p className="mt-8 text-lg text-[#5C665C] leading-relaxed max-w-md">
              Whether you're stocking a boutique or fitting out a chain, tell us
              what you need and we'll come back with a quote within a working day.
            </p>

            <div className="mt-14 space-y-8">
              {COMPANY.contacts.map((c) => (
                <div
                  key={c.raw}
                  className="border-t border-[#D1C9B8] pt-6 flex items-start justify-between gap-6"
                >
                  <div>
                    <p className="overline">Direct line</p>
                    <p className="font-serif text-3xl mt-2">{c.name}</p>
                  </div>
                  <a
                    href={`tel:${c.raw}`}
                    data-testid={`contact-phone-${c.name.toLowerCase()}`}
                    className="text-lg font-body link-underline"
                  >
                    {c.phone}
                  </a>
                </div>
              ))}
              <div className="border-t border-[#D1C9B8] pt-6 flex items-start justify-between gap-6">
                <div>
                  <p className="overline">Email</p>
                  <p className="font-serif text-3xl mt-2">Studio</p>
                </div>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="contact-email-link"
                  className="text-lg font-body link-underline break-all text-right"
                >
                  {COMPANY.email}
                </a>
              </div>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="md:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-[#EBE5D9] border border-[#D1C9B8] p-8 md:p-12"
            >
              <p className="overline">Inquiry form</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3">
                Tell us about your project.
              </h2>

              <div className="mt-10 grid sm:grid-cols-2 gap-6">
                <label className="block">
                  <span className="overline block mb-3">Full name</span>
                  <Input
                    data-testid="contact-input-name"
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your full name"
                    className="bg-transparent border-0 border-b border-[#1C2C22]/30 rounded-none h-11 px-0 focus-visible:ring-0 focus:border-[#1C2C22]"
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-3">Email</span>
                  <Input
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@company.com"
                    className="bg-transparent border-0 border-b border-[#1C2C22]/30 rounded-none h-11 px-0 focus-visible:ring-0 focus:border-[#1C2C22]"
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-3">Phone</span>
                  <Input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="Optional"
                    className="bg-transparent border-0 border-b border-[#1C2C22]/30 rounded-none h-11 px-0 focus-visible:ring-0 focus:border-[#1C2C22]"
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-3">Product interest</span>
                  <Select
                    value={form.product_interest}
                    onValueChange={(v) => update("product_interest", v)}
                  >
                    <SelectTrigger
                      data-testid="contact-select-product"
                      className="bg-transparent border-0 border-b border-[#1C2C22]/30 rounded-none h-11 px-0 focus:ring-0 focus:ring-offset-0 text-[#1C2C22]"
                    >
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent
                      data-testid="contact-select-content"
                      className="bg-[#F4F1EB] border-[#D1C9B8]"
                    >
                      {PRODUCTS.map((p) => (
                        <SelectItem
                          key={p.slug}
                          value={p.name}
                          data-testid={`contact-select-option-${p.slug}`}
                        >
                          {p.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="Multiple / Other">
                        Multiple / Other
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>

              <label className="block mt-8">
                <span className="overline block mb-3">Message</span>
                <Textarea
                  data-testid="contact-input-message"
                  required
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Volumes, timelines, print requirements, destination country…"
                  rows={5}
                  className="bg-transparent border border-[#1C2C22]/20 rounded-none focus-visible:ring-0 focus:border-[#1C2C22]"
                />
              </label>

              <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-[#5C665C] uppercase tracking-[0.2em]">
                  We reply within 24h · Mon–Sat
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-3 bg-[#1C2C22] text-[#F4F1EB] px-7 py-4 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#C27A62] disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send inquiry →"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
